// ─────────────────────────────────────────────────────────────────────────────
// Ground-distance helpers shared by anything that has to answer "how far is it
// from here to there" in metres.
//
// One copy, because the wrong answer is expensive: an under-reported leg is a
// mission dispatched with less battery margin than the operator was shown.
// ─────────────────────────────────────────────────────────────────────────────

import { MAPBOX_TOKEN } from '../config/mapStyles';

const EARTH_RADIUS_M = 6371000;

/** Place name → [lat, lon], or null. Mapbox when a token is set, else OpenStreetMap. */
export async function geocode(query) {
  const q = encodeURIComponent(String(query || '').trim());
  if (!q) return null;
  if (MAPBOX_TOKEN) {
    const res = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${q}.json?limit=1&access_token=${MAPBOX_TOKEN}`);
    const [lng, lat] = (await res.json())?.features?.[0]?.center || [];
    return Number.isFinite(lat) ? [lat, lng] : null;
  }
  const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${q}`);
  const hit = (await res.json())?.[0];
  return hit ? [parseFloat(hit.lat), parseFloat(hit.lon)] : null;
}

const toRad = (deg) => (deg * Math.PI) / 180;

const coord = (p) => {
  if (!p) return null;
  const lat = Number(p.lat);
  const lon = Number(p.lon !== undefined && p.lon !== null ? p.lon : p.lng);
  return Number.isFinite(lat) && Number.isFinite(lon) ? { lat, lon } : null;
};

/** Great-circle distance in metres between two { lat, lon } points. */
export function haversineMeters(a, b) {
  const p = coord(a);
  const q = coord(b);
  if (!p || !q) return 0;

  const dLat = toRad(q.lat - p.lat);
  const dLon = toRad(q.lon - p.lon);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(p.lat)) * Math.cos(toRad(q.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(s)));
}

/**
 * Metres flown from `start`, through each stop in order, and back to `start`.
 * Returns 0 when the start point or every stop is unusable — the caller shows
 * "unknown", which is honest, instead of a fabricated figure.
 */
export function roundTripMeters(start, stops) {
  const from = coord(start);
  const pts = (stops || []).map(coord).filter(Boolean);
  if (!from || pts.length === 0) return 0;

  let meters = 0;
  let prev = from;
  pts.forEach((p) => { meters += haversineMeters(prev, p); prev = p; });
  return meters + haversineMeters(prev, from); // return home
}
